import { useEffect, useState, useCallback } from "react";
import { Loader2, KeyRound, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { GlassCard, RoleBadge } from "../../components/common/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const ROLES = ["super_admin", "manager", "staff", "customer"];

export default function AdminUsers() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/users", { params: { q: q || undefined } }).then(({ data }) => setUsers(data)).catch(() => {}).finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const changeRole = async (u, role) => {
    try { await api.patch(`/admin/users/${u.user_id}/role`, { role }); toast.success(`${u.name} is now ${role}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change role"); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/admin/users/${u.user_id}/status`, { is_active: !(u.is_active !== false) }); toast.success("Account updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update account"); }
  };
  const sendReset = async (u) => {
    try { await api.post(`/admin/users/${u.user_id}/reset-link`); toast.success(`Reset link sent to ${u.email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send reset link"); }
  };
  const deleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.user_id}`);
      toast.success(`${deleteTarget.email} deleted — a notification email was sent.`);
      setDeleteTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete account"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div>
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">Users & Roles</h1>
        <p className="mt-2 text-muted-foreground">All registered accounts and their access levels.</p>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
        <Input placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-black/30 border-white/15 text-white pl-9" data-testid="user-search" />
      </div>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading users…</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/70">Name</TableHead>
                  <TableHead className="text-white/70">Email</TableHead>
                  <TableHead className="text-white/70">Role</TableHead>
                  <TableHead className="text-white/70">Verified</TableHead>
                  <TableHead className="text-white/70">Active</TableHead>
                  <TableHead className="text-white/70">Registered</TableHead>
                  <TableHead className="text-white/70">Last login</TableHead>
                  <TableHead className="text-white/70">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} className="border-white/10" data-testid={`user-row-${u.email}`}>
                    <TableCell className="text-white font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      {isSuper ? (
                        <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="bg-black/30 border border-white/15 text-white rounded-md px-2 py-1 text-xs" data-testid={`role-select-${u.email}`}>
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : <RoleBadge role={u.role} />}
                    </TableCell>
                    <TableCell className={u.is_verified ? "text-emerald-400" : "text-firered"}>{u.is_verified ? "Yes" : "No"}</TableCell>
                    <TableCell><Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`active-toggle-${u.email}`} /></TableCell>
                    <TableCell className="text-muted-foreground text-xs">{(u.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{(u.last_login_at || "").slice(0, 10) || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {u.auth_provider === "email" && (
                          <Button size="sm" variant="outline" className="border-white/20 text-white gap-1 text-xs" onClick={() => sendReset(u)} data-testid={`reset-${u.email}`}><KeyRound className="h-3 w-3" />Reset</Button>
                        )}
                        {isSuper && u.role === "customer" && (
                          <Button size="sm" variant="outline" className="border-firered/50 text-firered hover:bg-firered/10 gap-1 text-xs" onClick={() => setDeleteTarget(u)} data-testid={`delete-${u.email}`}><Trash2 className="h-3 w-3" />Delete</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </GlassCard>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-navy border-white/15" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2"><Trash2 className="h-5 w-5 text-firered" /> Delete customer account</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              This will permanently delete the account for <span className="text-white font-medium">{deleteTarget?.email}</span> and revoke their login. Their past reservations are kept for records. An email will be sent notifying them the account was deleted by an administrator. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 text-white bg-transparent">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user-btn" disabled={deleting} onClick={(e) => { e.preventDefault(); deleteUser(); }} className="bg-firered hover:bg-firered/90 text-white">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
